"use client";

import { addLeadingSlash } from "@frend-digital/utils";
import { Result } from "@frend-digital/utils/results";
import { useParams } from "next/navigation";
import { useActionState } from "react";
import { useRouting } from "../client";
import type { Route } from "../route-manifest";
import {
  findDomain,
  getLang,
  parseGeo,
  replaceLocale,
  type GeoLocale,
} from "../shared";
import { usePathname, useRouter } from "./AppRouterLibs";
import type { Alternate } from "./types";

function getAlternates(): Alternate[] {
  const alternates = document.querySelectorAll<HTMLLinkElement>(
    "link[rel=alternate]"
  );
  const alternateArray = Array.from(alternates).map((link) => {
    return {
      href: link.href,
      hrefLang: link.hreflang,
      lang: link.lang,
      url: new URL(link.href, window.location.origin),
    };
  });

  const routeManifest = document.querySelector<HTMLMetaElement>(
    "meta[name='x-route-manifest']"
  );

  const routeManifestJson = routeManifest?.getAttribute("content");

  const currentOrigin =
    process.env.NODE_ENV === "development"
      ? window.location.host
      : window.location.origin;

  if (routeManifestJson) {
    try {
      const routeManifestObject = JSON.parse(routeManifestJson) as Route[];

      routeManifestObject.forEach(({ href, hrefLang, lang }) => {
        alternateArray.push({
          href,
          hrefLang,
          lang,
          url: new URL(href, currentOrigin),
        });
      });
    } catch (error) {
      console.error("Could not set manifest", error);
    }
  }

  return alternateArray;
}

function findAlternate(alternates: Alternate[], geo: GeoLocale) {
  const foundAlternate = alternates.find((alternate) => {
    return (
      alternate.hrefLang.startsWith(`${getLang(geo)}-`) ||
      alternate.hrefLang === getLang(geo)
    );
  });

  return foundAlternate;
}

export function useGeo({ useAlternates = true, debug = false } = {}) {
  const router = useRouter();
  const pathname = usePathname();
  const { geo } = useParams<{ geo: GeoLocale }>();
  const parsed = Result.unwrapOr(parseGeo(geo), null);
  const routing = useRouting();

  const [, action, pending] = useActionState(
    (_: true | false | null, newGeo: GeoLocale) => {
      try {
        let pathnameToUse: string = pathname;
        if (useAlternates) {
          // Find the alternate href
          const alternateList = getAlternates();

          const alternate = findAlternate(alternateList, newGeo);
          if (debug) {
            console.log("[useGeo] - alternates", alternateList);
            console.log("[useGeo] - found alternate", alternate);
          }
          if (alternate) {
            pathnameToUse = alternate.url.pathname;
            if (debug) {
              console.log(
                "[useGeo] - alternate found - new path",
                pathnameToUse
              );
            }
          }
        }

        const domain = findDomain(newGeo, routing);

        if (domain && window.location.host !== domain.domain) {
          const path = replaceLocale(pathnameToUse, newGeo, routing, newGeo);
          if (debug) {
            console.log("[useGeo] - domain found - new path", path);
          }
          window.location.href = `https://${domain.domain}${addLeadingSlash(path)}`;

          return true;
        }

        if (debug) {
          console.log(
            "[useGeo] - performing router replace",
            pathnameToUse,
            newGeo
          );
        }

        router.replace(pathnameToUse, {
          locale: newGeo,
        });
        return true;
      } catch (error) {
        console.log("useGeo - error", error);
        return false;
      }
    },
    null
  );

  if (!parsed) {
    throw new Error("Invalid geo detected");
  }

  return {
    action: action,
    geo,
    ...parsed,
    pending,
  };
}
